import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Landmark, Loader2 } from "lucide-react";
import { Suspense } from "react";
import { toast } from "sonner";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { FinanceiroLabels } from "@/lib/labels/financeiro";

import { FinanceListSkeleton, FinanceMetricsSkeleton } from "./area-skeleton";
import { MetricCard, StatePill } from "./primitives";
import { FinanceTimeline, FinanceTimelineCard } from "./timeline";
import type { FinanceModal } from "./types";

const CLASSIFICATION = {
	receipt: { label: "Recebimento", tone: "green" },
	bill: { label: "Conta paga", tone: "green" },
	settlement: { label: "Repasse de cartão", tone: "green" },
	cash_deposit: { label: "Depósito do caixa", tone: "green" },
	ignored: { label: "Ignorado", tone: "muted" },
	pending: { label: "Em processamento", tone: "muted" },
	unmatched: { label: "A resolver", tone: "amber" },
} as const;

interface BankAreaProps {
	month: string;
	page: number;
	account: number | undefined;
	onPage: (page: number) => void;
	onAccount: (account?: number) => void;
	onModal: (modal: FinanceModal) => void;
}

export function BankArea(props: BankAreaProps) {
	const scope = `${props.month}:${props.account ?? "all"}`;

	return (
		<>
			<BankAccountFilter account={props.account} onAccount={props.onAccount} />
			<ErrorBoundary key={scope}>
				<Suspense fallback={<FinanceMetricsSkeleton />}>
					<BankSummary month={props.month} account={props.account} />
				</Suspense>
			</ErrorBoundary>
			<ErrorBoundary key={`${scope}:${props.page}`}>
				<Suspense fallback={<FinanceListSkeleton />}>
					<BankList {...props} />
				</Suspense>
			</ErrorBoundary>
		</>
	);
}

function BankAccountFilter({
	account,
	onAccount,
}: {
	account: number | undefined;
	onAccount: (account?: number) => void;
}) {
	const { data: bankAccounts } = useQuery(
		orpc.financeiro.treasury.statementAccounts.queryOptions(),
	);

	if (bankAccounts === undefined || (account === undefined && bankAccounts.length < 2)) {
		return null;
	}

	return (
		<div className="mt-5 flex flex-wrap gap-2" data-component="bank-account-filter">
			<Button
				size="sm"
				data-slot="account-all"
				variant={account === undefined ? "default" : "outline"}
				onClick={() => onAccount()}
			>
				Todas as contas
			</Button>
			{bankAccounts.map((option) => (
				<Button
					key={option.id}
					size="sm"
					data-slot={`account-${option.id}`}
					data-active={option.active}
					variant={account === option.id ? "default" : "outline"}
					onClick={() => onAccount(option.id)}
				>
					{option.name}
					{!option.active && " (desativada)"}
				</Button>
			))}
		</div>
	);
}

function BankSummary({ month, account }: { month: string; account: number | undefined }) {
	const { data } = useSuspenseQuery(
		orpc.financeiro.bank.summary.queryOptions({
			input: { month, treasury_account_id: account },
		}),
	);

	return (
		<div className="mt-5 grid gap-3 sm:grid-cols-3">
			<MetricCard
				tone="hero"
				icon={Landmark}
				label="Resultado no banco"
				value={FinanceiroLabels.signed(data.net_cents)}
				detail={`${data.total} movimentos`}
			/>
			<MetricCard
				icon={ArrowDownLeft}
				label="Entradas"
				value={FinanceiroLabels.money(data.income_cents)}
			/>
			<MetricCard
				icon={ArrowUpRight}
				label="Saídas"
				value={FinanceiroLabels.money(data.expense_cents)}
			/>
		</div>
	);
}

function BankList({ month, page, account, onPage, onModal }: BankAreaProps) {
	const queryClient = useQueryClient();
	const { data } = useSuspenseQuery(
		orpc.financeiro.bank.list.queryOptions({
			input: { month, page, treasury_account_id: account },
		}),
	);
	const refresh = async () => {
		await queryClient.invalidateQueries({ queryKey: orpc.financeiro.key() });
	};
	const { mutate: setIgnored, isPending: isIgnoring } = useMutation(
		orpc.financeiro.bank.ignore.mutationOptions({
			onSuccess: refresh,
			onError: () => toast.error("Não foi possível alterar o movimento"),
		}),
	);

	return (
		<>
			{data.data.length === 0 && (
				<div className="surface-panel mt-5">
					<EmptyState
						icon={Landmark}
						title="Nenhum movimento neste mês"
						description="Atualize o extrato para buscar os dados mais recentes do banco."
					/>
				</div>
			)}
			{data.data.length > 0 && (
				<FinanceTimeline
					items={data.data}
					dateFor={(transaction) => transaction.effective_on}
					itemKey={(transaction) => transaction.id}
					renderItem={(transaction) => {
						const classification = CLASSIFICATION[transaction.classification];
						const signedAmount =
							transaction.direction === "income"
								? transaction.amount_cents
								: -transaction.amount_cents;
						const Icon = transaction.direction === "income" ? ArrowDownLeft : ArrowUpRight;
						const detail = [transaction.account_name, transaction.method].filter(
							(part) => part !== null,
						);

						return (
							<FinanceTimelineCard
								icon={Icon}
								title={
									transaction.person_name ??
									transaction.bill_description ??
									transaction.description ??
									"Movimento bancário"
								}
								detail={detail.length === 0 ? "Método não informado" : detail.join(" - ")}
								note={transaction.note}
								amount={FinanceiroLabels.signed(signedAmount)}
								amountTone={transaction.direction === "income" ? "positive" : "default"}
								status={<StatePill label={classification.label} tone={classification.tone} />}
								actions={
									(transaction.classification === "unmatched" ||
										transaction.classification === "ignored") && (
										<>
											{transaction.classification === "unmatched" && (
												<Button
													variant="outline"
													size="sm"
													onClick={() => onModal({ kind: "bank-resolution", id: transaction.id })}
												>
													{transaction.direction === "income" && "Resolver entrada"}
													{transaction.direction === "expense" && "Resolver saída"}
												</Button>
											)}
											<Button
												variant="ghost"
												size="sm"
												disabled={isIgnoring}
												onClick={() =>
													setIgnored({
														id: transaction.id,
														ignored: transaction.classification !== "ignored",
													})
												}
											>
												{isIgnoring && <Loader2 className="animate-spin" />}
												{transaction.classification === "ignored" && "Restaurar"}
												{transaction.classification !== "ignored" && "Ignorar"}
											</Button>
										</>
									)
								}
							/>
						);
					}}
					page={page}
					pages={data.pages}
					onPage={onPage}
				/>
			)}
		</>
	);
}
